// @ts-nocheck
import React, { useState, useMemo, useCallback } from 'react';
import { ArrowLeft, FileText, Globe, ChevronDown, ChevronRight, X, Search, Save, Pencil, Info, Trash2 } from 'lucide-react';
import { PAWRecord } from '../../types';
import { PAWRecordDetail } from '../../components/cybersweep/PAWRecordDetail';
import { ChecklistPiiMultiSelect } from '../../components/cybersweep/ChecklistPiiMultiSelect';
import WebsiteInfoModal from '../../components/cybersweep/WebsiteInfoModal';
import CSClientProfileModal, { ClientProfileData } from '../../components/cybersweep/CSClientProfileModal';
import { CSAuditEditModal } from '../../components/modals/CSAuditEditModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { useCSAudit, usePAWRecordsInfinite, usePAWRecordCount, useUpdateCSAudit, useCreateCSAudit, useDeleteCSAudit, useCreatePAWRecord, useCreatePAWRecordsBatch, useUpdatePAWRecord, useUpdateCyberSweepClient, useAccountIdToName, lookupAccountDisplayName, useCyberSweepClient, useAuditTemplates, fetchAllPAWRecordsForAudit, fetchCSAuditsForClientKeysMerged } from '../../services/queries/cyberSweepQueries';
import { WEBSITE_CATEGORIES, WebsiteConfiguration, getWebsitesWithMinimalFlag, getMinimalWebsiteCount, WebsiteWithMinimalFlag } from '../../data/websiteConfigurations';
import { AUDIT_TEMPLATES } from '../../data/auditTemplates';
import type { AuditTemplate } from '../../types/auditTemplate';
import { useUsers } from '../../services/queries/userQueries';
import { GobodoRequiredGuard } from '../../components/common/GobodoRequiredGuard';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { StandardTextarea } from '../../components/standard';
import {
  processAuditWorkflow,
  CreateCSAuditParams,
  CSAuditRecord as BusinessLogicCSAuditRecord,
  PAWRecord as BusinessLogicPAWRecord,
  AccountRecord as BusinessLogicAccountRecord,
} from '../../services/cybersweep/auditBusinessLogic';
import { deriveChecklistPhaseDefaults } from '../../services/cybersweep/checklistPhase';
import {
  parsePiiFoundValue,
  serializePiiFoundValue,
} from '../../services/cybersweep/piiFound';
import {
  getGobodoRecordById,
  getGobodoRecordByKey,
} from '../../services/gobodo/gobodoRecordService';
import { getCustomSuffix } from '../../config/gobodoConfig';
import { readCSAuditClientNameFromClientTraversal } from '../../utils/cyberSweepClientBoardTraversalFields';

// Note type for CS Audit
interface CSAuditNote {
  id: string;
  body: string;
  createdBy: string;
  createdDate: string;
  lastModifiedBy?: string;
  lastModifiedDate?: string;
}

interface CyberSweepAuditDetailProps {
  auditId: string;
  onBack: () => void;
}

// Field resolver: checks multiple naming conventions (camelCase, underscore SF names, custom__ prefix)
const g = (r: Record<string, unknown>, ...keys: string[]): unknown => {
  for (const k of keys) {
    if (r[k] !== undefined && r[k] !== null) return r[k];
  }
  return undefined;
};
// Check if string is a system-generated label (not a real name)
const isGobodoLabel = (s: string) =>
  /^(CyberAudit|CSAudit|CSA|PAWRecord|Client|PAW)-\d+$/i.test(s) || // Auto-generated names like CSA-92562
  /^a\d[a-z]\d/i.test(s) || // Salesforce IDs like a1Z...
  /^Record\s+\d+$/i.test(s); // "Record 123" placeholders

function auditFieldDisplay(raw: string, mode: 'date' | 'datetime' | 'text' = 'text'): string {
  const s = (raw ?? '').trim();
  if (!s) return '—';
  if (mode === 'text') return s;

  // Normalize date strings for consistent parsing:
  // 1. Date-only (YYYY-MM-DD): Treat as local midnight, not UTC
  // 2. DateTime without Z (YYYY-MM-DDTHH:MM): Append 'Z' to treat as UTC
  let dateStr = s;

  // Check if it's a date-only string (no T)
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    // Date-only: add T00:00:00 to parse as local time (not UTC)
    dateStr = s + 'T00:00:00';
  } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s) && !s.endsWith('Z') && !s.includes('+') && !s.includes('-', 10)) {
    // DateTime without timezone: append 'Z' to treat as UTC
    dateStr = s + 'Z';
  }

  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return s;
  // Use undefined locale for user's local timezone
  if (mode === 'date')
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  // Format datetime without seconds for consistency
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const mapRecordToAuditData = (r: Record<string, unknown> | null, nameMap?: Record<string, string>) => {
  if (!r) return null;
  const fmt = (v: unknown, def: string) => (v != null ? String(v) : def);
  const d = (r.data as Record<string, unknown>) ?? {};
  /** Gobodo often nests field values on `data`; merge with root. */
  const gg = (...keys) => g(r, ...keys) ?? g(d, ...keys);
  const rawClientName = fmt(gg('clientName', 'ClientName__c', 'AccountName__c'), '');
  const recFirst = fmt(gg('firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name', 'first_name'), '');
  const recLast = fmt(gg('lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name', 'last_name'), '');
  const fullNameFromParts = [recFirst, recLast].filter(Boolean).join(' ').trim();
  const recordLabel = fmt(gg('label'), '');
  const accountRef = fmt(gg('relatedClientId', 'clientId', 'ClientId__c', 'Account__c', 'custom__account'), '');
  const lookupName = lookupAccountDisplayName(nameMap, accountRef);
  const clientName = (rawClientName && !isGobodoLabel(rawClientName) ? rawClientName : '') ||
                     (fullNameFromParts && !isGobodoLabel(fullNameFromParts) ? fullNameFromParts : '') ||
                     (recordLabel && !isGobodoLabel(recordLabel) && !recordLabel.startsWith('Record ') ? recordLabel : '') ||
                     (lookupName && !isGobodoLabel(lookupName) ? lookupName : '') || 'Unknown';
  return {
    id: fmt(r.id, ''),
    name: fmt(gg('name', 'Name__c'), fmt(r.id, '')),
    clientId: accountRef,
    clientName,
    assignedAnalyst: fmt(gg('assignedAnalyst', 'Assigned_Analyst__c', 'AssignedAnalyst__c'), ''),
    auditor: fmt(gg('auditor', 'Auditor__c', 'custom__auditor'), ''),
    dueDate: fmt(gg('dueDate', 'Due_Date__c', 'DueDate__c', 'custom__due_date'), ''),
    fiscalYear: fmt(gg('fiscalYear', 'Fiscal_Year__c', 'FiscalYear__c', 'custom__fiscal_year'), ''),
    auditType: fmt(gg('Audit_Type_Name__c', 'auditTypeName', 'auditTypeDisplayName', 'AuditTypeDisplayName__c', 'auditType', 'Audit_Type__c', 'AuditType__c', 'custom__audit_type'), ''),
    auditPhase: fmt(gg('auditPhase', 'Audit_Phase__c', 'AuditPhase__c', 'custom__audit_phase'), ''),
    phase1DueDate: fmt(gg('phase1DueDate', 'Phase_1_Due_Date__c', 'Phase1DueDate__c'), ''),
    phase2DueDate: fmt(gg('phase2DueDate', 'Phase_2_Due_Date__c', 'Phase2DueDate__c'), ''),
    phaseStatus: fmt(gg('phaseStatus', 'Phase_Status__c', 'PhaseStatus__c', 'custom__phase_status'), ''),
    status: fmt(gg('status', 'Status__c'), ''),
    roadblockNotes: fmt(gg('roadblockNotes', 'Road_Block_Note__c', 'RoadblockNotes__c'), ''),
    roadBlockNote: fmt(gg('roadBlockNote', 'Road_Block_Note__c', 'RoadBlockNote__c', 'custom__road_block_note'), ''),
    phase1StartDateTime: fmt(
      gg(
        'phase1StartDateTime',
        'Phase_1_Start_Date_Time__c',
        'Phase1StartDateTime__c',
        'Phase_1_Start_Date__c',
        'custom__phase_1_start_date_time',
        'custom__phase_1_start'
      ),
      ''
    ),
    phase1CompletedDateTime: fmt(
      gg(
        'phase1CompletedDateTime',
        'Phase_1_Completed_Date_Time__c',
        'Phase1CompletedDateTime__c',
        'Phase_1_Completed_Date__c',
        'custom__phase_1_completed_date_time',
        'custom__phase_1_completed'
      ),
      ''
    ),
    phase2StartDateTime: fmt(
      gg(
        'phase2StartDateTime',
        'Phase_2_Start_Date_Time__c',
        'Phase2StartDateTime__c',
        'Phase_2_Start_Date__c',
        'custom__phase_2_start_date_time',
        'custom__phase_2_start'
      ),
      ''
    ),
    phase2CompletedDateTime: fmt(
      gg(
        'phase2CompletedDateTime',
        'Phase_2_Completed_Date_Time__c',
        'Phase2CompletedDateTime__c',
        'Phase_2_Completed_Date__c',
        'custom__phase_2_completed_date_time',
        'custom__phase_2_completed'
      ),
      ''
    ),
    websiteCount: Number(gg('websiteCount', 'Website_Count__c', 'WebsiteCount__c') ?? 0),
    completionDate: (gg('completionDate', 'Completion_Date__c', 'CompletionDate__c') ?? null) as string | null,
    // System fields - try name fields first, then ID fields
    createdBy: fmt(gg('createdBy', 'CreatedByName__c', 'CreatedBy__c', 'CreatedById__c', 'recordCreatedBy'), ''),
    createdDate: fmt(gg('createdDate', 'CreatedDate__c', 'CreatedDate'), ''),
    lastModified: fmt(gg('lastModified', 'LastModifiedDate__c', 'LastModifiedDate'), ''),
    lastModifiedBy: fmt(gg('lastModifiedBy', 'LastModifiedByName__c', 'LastModifiedBy__c', 'LastModifiedById__c'), ''),
    lastModifiedDate: fmt(gg('lastModifiedDate', 'LastModifiedDate__c', 'LastModifiedDate'), ''),
  };
};

const mapToPAWRecord = (r: Record<string, unknown>): PAWRecord => ({
  id: String(r.id ?? r.Id ?? ''),
  csAuditId: String(g(r, 'csAuditId', 'CS_Audit__c', 'CSAuditRelField__c', 'custom__cs_audit') ?? ''),
  clientId: String(g(r, 'clientId', 'ClientId__c', 'Account__c', 'custom__account') ?? ''),
  clientName: String(g(r, 'clientName', 'ClientName__c') ?? ''),
  auditTemplate: String(g(r, 'auditTemplate', 'Audit_Template__c', 'AuditTemplate__c') ?? ''),
  website: String(g(r, 'websiteDisplayName', 'WebsiteDisplayName__c', 'website', 'Website__c', 'custom__website') ?? ''),
  websiteDisplayName: String(g(r, 'websiteDisplayName', 'WebsiteDisplayName__c') ?? ''),
  websiteLookupId: String(g(r, 'websiteLookupId', 'AuditWebsiteRelField__c', 'Audit_Website__c', 'custom__audit_website') ?? ''),
  websiteUrl: String(g(r, 'websiteUrl', 'Website_URL__c', 'URL__c', 'custom__url', 'Opt_Out_URL__c') ?? ''),
  phase2Required: Boolean(g(r, 'phase2Required', 'Phase_2_Required__c', 'Phase2Required__c')),
  createdBy: String(g(r, 'createdBy', 'CreatedByName__c', 'recordCreatedBy', 'CreatedBy', 'Created_By__c', 'CreatedBy__c', 'CreatedById') ?? ''),
  auditPhase: String(g(r, 'auditPhase', 'Audit_Phase__c', 'AuditPhase__c') ?? ''),
  findings: String(g(r, 'findings', 'Findings__c') ?? ''),
  method: String(g(r, 'method', 'Method__c') ?? ''),
  piiFound: g(r, 'piiFound', 'PII_Found__c', 'PIIFound__c') as string | boolean,
  // clientInfoLinks - can be string (from API) or array (legacy/mock data)
  clientInfoLinks: g(r, 'clientInfoLinks', 'Client_Info_Links__c', 'ClientInfoLinks__c') as string | string[] ?? '',
  lastModifiedBy: String(g(r, 'lastModifiedBy', 'LastModifiedBy__c') ?? ''),
  lastModifiedDate: String(g(r, 'lastModifiedDate', 'LastModifiedDate__c') ?? ''),
  createdDate: String(g(r, 'createdDate', 'CreatedDate__c') ?? ''),
  status: String(g(r, 'status', 'Status__c') ?? ''),
});

const extractExistingWebsiteNames = (records: Record<string, unknown>[]): string[] =>
  records
    .map((record) => {
      const paw = mapToPAWRecord(record);
      return String(paw.websiteDisplayName || paw.website || '').trim();
    })
    .filter((name) => name.length > 0);

// ─── PII items for the dual-list box ──────────────────────────────────────
const PII_OPTIONS = [
  'Full Name', 'Address', 'Date of Birth', 'Phone Number', 'Email',
  'Business Address', 'Business Name', 'Mugshot', 'ID Number',
  'VIN', 'Debit/Credit Card Numbers',
];

const AUDIT_PHASE_OPTIONS = ['--None--', 'Phase 1', 'Phase 2'];

// Dropdown options for inline editing - Audit Type
const AUDIT_TYPE_OPTIONS = [
  'Initial Audit',
  'Follow-up Audit',
  'Phase 1',
  'Phase 2',
  'Quarterly Audit',
  'Annual Review',
  'Special Request',
];

// Phase Status options
const PHASE_STATUS_OPTIONS = [
  'Not Started',
  'P1 - In Progress',
  'P1 - Road Block',
  'P1 - Completed',
  'P2 - In Progress',
  'P2 - Road Block',
  'P2 - Awaiting Assignment',
  'P2 - Audit Checklist',
  'Audit Completed',
];

// Findings options for inline editing (Phase 3 spec)
const FINDINGS_OPTIONS = [
  { value: '', label: '-- Select --' },
  { value: 'Listing/s found', label: 'Listing/s found' },
  { value: 'Listing/s found and deletion has been requested', label: 'Listing/s found and deletion requested' },
  { value: 'No info found', label: 'No info found' },
  { value: 'Listing removed', label: 'Listing removed' },
  { value: 'Listing still active', label: 'Listing still active' },
  { value: 'Unable to verify', label: 'Unable to verify' },
  { value: 'Opt-out submitted', label: 'Opt-out submitted' },
  { value: 'Pending verification', label: 'Pending verification' },
];
// Legacy findings options for NewPAWRecordModal (keep for backwards compatibility)
const FINDINGS_OPTIONS_LEGACY = [
  '--None--', 'Info not found', 'Listings found and deletion requested',
  'Site not working', 'Site deleted info', 'Site not responding',
  'Site not honoring', 'Incomplete', 'Database not searchable', 'Listing/s not found',
];
const METHOD_OPTIONS = [
  '--None--', 'Site Opt-out form', 'Site Contact Us page', 'Email', 'Phone call', 'Fax',
];

// ─── New PAW Record Modal ─────────────────────────────────────────────────
interface NewPAWModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditId: string;
  auditName: string;
  onSave: (data: Record<string, unknown>) => void;
}

const NewPAWRecordModal: React.FC<NewPAWModalProps> = ({
  isOpen, onClose, auditId, auditName, onSave,
}) => {
  const [form, setForm] = useState({
    websiteName: '',
    auditPhase: '--None--',
    findings: '--None--',
    method: '--None--',
    auditTemplate: '',
    website: '',
    phase2Required: false,
    clientInfoLinks: '',
  });
  const [piiAvailable, setPiiAvailable] = useState<string[]>([...PII_OPTIONS]);
  const [piiChosen, setPiiChosen] = useState<string[]>([]);
  const [selectedAvailable, setSelectedAvailable] = useState<string | null>(null);
  const [selectedChosen, setSelectedChosen] = useState<string | null>(null);

  const set = (field: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const moveRight = () => {
    if (!selectedAvailable) return;
    setPiiAvailable((p) => p.filter((i) => i !== selectedAvailable));
    setPiiChosen((p) => [...p, selectedAvailable]);
    setSelectedAvailable(null);
  };
  const moveLeft = () => {
    if (!selectedChosen) return;
    setPiiChosen((p) => p.filter((i) => i !== selectedChosen));
    setPiiAvailable((p) => [...p, selectedChosen]);
    setSelectedChosen(null);
  };

  const handleSave = (andNew: boolean) => {
    onSave({
      ...form,
      csAuditId: auditId,
      piiFound: piiChosen,
    });
    if (andNew) {
      setForm({ websiteName: '', auditPhase: '--None--', findings: '--None--', method: '--None--', auditTemplate: '', website: '', phase2Required: false, clientInfoLinks: '' });
      setPiiAvailable([...PII_OPTIONS]);
      setPiiChosen([]);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">New Performed Audit Website</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          <p className="text-xs text-right text-red-500 mb-4">* = Required Information</p>

          {/* Information section */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg mb-6">
            <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/40 text-sm font-semibold text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700">
              Information
            </div>
            <div className="p-4 grid grid-cols-2 gap-x-6 gap-y-4">
              {/* Row 1 */}
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Performed Audit Website Name</label>
                <input
                  type="text"
                  value={form.websiteName}
                  onChange={(e) => set('websiteName', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span className="text-red-500">*</span> Audit Phase
                </label>
                <select
                  value={form.auditPhase}
                  onChange={(e) => set('auditPhase', e.target.value)}
                  className="w-full border border-blue-500 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {AUDIT_PHASE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {/* Row 2: CS Audit (readonly chip) + Findings */}
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span className="text-red-500">*</span> CS Audit
                </label>
                <div className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 bg-gray-50 dark:bg-gray-700">
                  <FileText className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-900 dark:text-gray-100 flex-1">{auditName || auditId}</span>
                  <button className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Findings</label>
                <select
                  value={form.findings}
                  onChange={(e) => set('findings', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {FINDINGS_OPTIONS_LEGACY.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {/* Row 3: Audit Template + Method */}
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span className="text-red-500">*</span> Audit Template
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.auditTemplate}
                    onChange={(e) => set('auditTemplate', e.target.value)}
                    placeholder="Search Cyber Sweep Audit Templates..."
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 pr-8"
                  />
                  <Search className="absolute right-2.5 top-2 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Method</label>
                <select
                  value={form.method}
                  onChange={(e) => set('method', e.target.value)}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {METHOD_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>

              {/* Row 4: Website + PII Found dual-list */}
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                  <span className="text-red-500">*</span> Website
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={form.website}
                    onChange={(e) => set('website', e.target.value)}
                    placeholder="Search Audit Website Configurations..."
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 pr-8"
                  />
                  <Search className="absolute right-2.5 top-2 h-4 w-4 text-gray-400" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1 font-semibold">PII Found</label>
                <div className="flex gap-2">
                  {/* Available */}
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Available</p>
                    <div className="border border-gray-300 dark:border-gray-600 rounded h-28 overflow-y-auto">
                      {piiAvailable.map((item) => (
                        <div
                          key={item}
                          onClick={() => setSelectedAvailable(item)}
                          className={`px-2 py-1 text-xs cursor-pointer ${selectedAvailable === item ? 'bg-blue-100 dark:bg-blue-900/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Arrows */}
                  <div className="flex flex-col items-center justify-center gap-1">
                    <button onClick={moveRight} className="p-1 border border-gray-300 rounded hover:bg-gray-100 text-xs">&#9654;</button>
                    <button onClick={moveLeft} className="p-1 border border-gray-300 rounded hover:bg-gray-100 text-xs">&#9664;</button>
                  </div>
                  {/* Chosen */}
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1">Chosen</p>
                    <div className="border border-gray-300 dark:border-gray-600 rounded h-28 overflow-y-auto">
                      {piiChosen.map((item) => (
                        <div
                          key={item}
                          onClick={() => setSelectedChosen(item)}
                          className={`px-2 py-1 text-xs cursor-pointer ${selectedChosen === item ? 'bg-blue-100 dark:bg-blue-900/40' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Row 5: Phase 2 Required + Link(s) */}
              <div>
                <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                  Phase 2 Required
                </label>
                <input
                  type="checkbox"
                  checked={form.phase2Required}
                  onChange={(e) => set('phase2Required', e.target.checked)}
                  className="mt-1 rounded"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Link(s) for client information</label>
                <textarea
                  value={form.clientInfoLinks}
                  onChange={(e) => set('clientInfoLinks', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button onClick={() => handleSave(true)} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 text-gray-700 dark:text-gray-300">
            Save &amp; New
          </button>
          <button onClick={() => handleSave(false)} className="px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 shadow-sm active:scale-[0.98] transition-all">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Add Audit Checklist Modal ─────────────────────────────────────────────
interface AddAuditChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  auditId: string;
  currentTemplate?: string; // The CS Audit's current Audit_Template__c value
  existingWebsites: string[];
  onAddWebsites: (websites: WebsiteConfiguration[], templateName: string) => Promise<void>;
  /** Live progress while bulk-creating PAW rows (optional). */
  batchProgress?: { completed: number; total: number } | null;
}

const AddAuditChecklistModal: React.FC<AddAuditChecklistModalProps> = ({
  isOpen, onClose, auditId, currentTemplate, existingWebsites, onAddWebsites, batchProgress,
}) => {
  // Fetch audit templates from Gobodo (prefer real templates over hardcoded)
  const { data: gobodoTemplates = [] } = useAuditTemplates();
  
  // Standard audit type names in display order
  const STANDARD_AUDIT_TYPES_ORDER = [
    'Initial Deletion',
    'Initial Audit', 
    'Final Audit',
    'Quarter #1 Audit',
    'Quarter #2 Audit',
    'Quarter #3 Audit',
    'Quarter #4 Audit',
  ];

  // Use Gobodo templates if available, fallback to local AUDIT_TEMPLATES
  const availableTemplates = useMemo(() => {
    if (gobodoTemplates.length > 0) {
      // Filter to only standard audit types and deduplicate by name
      const seen = new Set<string>();
      const filtered = gobodoTemplates
        .filter(t => {
          const isStandard = STANDARD_AUDIT_TYPES_ORDER.includes(t.name);
          const isDuplicate = seen.has(t.name);
          if (isStandard && !isDuplicate) {
            seen.add(t.name);
            return true;
          }
          return false;
        })
        .map(t => ({
          id: t.key || t.id,
          name: t.name,
          phase1DueDateDays: t.phase1DueDateDays,
          phase2DueDateDays: t.phase2DueDateDays,
          phase2Required: t.phase2Required,
          checklistType: t.checklistType as 'initial_deletion' | 'comprehensive' | 'minimal',
          gobodoKey: t.key,
        }));
      // Sort by the defined order
      return filtered.sort((a, b) => 
        STANDARD_AUDIT_TYPES_ORDER.indexOf(a.name) - STANDARD_AUDIT_TYPES_ORDER.indexOf(b.name)
      );
    }
    return AUDIT_TEMPLATES.map(t => ({ ...t, gobodoKey: undefined }));
  }, [gobodoTemplates]);

  // Step 1: Template Selection, Step 2: Website Selection
  const [step, setStep] = useState<1 | 2>(1);
  // Pre-select template if current audit has one set
  const [selectedTemplate, setSelectedTemplate] = useState<AuditTemplate | null>(() => {
    if (currentTemplate) {
      // Try to match by name from either Gobodo or local templates
      return AUDIT_TEMPLATES.find(t => t.name === currentTemplate) || null;
    }
    return null;
  });
  const [selectedWebsites, setSelectedWebsites] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CEO directive: All templates now use minimal (77) sites only.
  // Get all websites with minimal flag - minimal sites are enabled, others grayed out
  const allWebsitesWithFlag = useMemo(() => {
    return getWebsitesWithMinimalFlag();
  }, []);

  // Filter out already-added websites and apply search/category filters
  // Shows ALL websites but marks non-minimal as disabled (grayed out)
  const filteredWebsites = useMemo(() => {
    return allWebsitesWithFlag.filter(website => {
      // Exclude websites already in the audit
      const alreadyAdded = existingWebsites.some(
        existing => existing.toLowerCase() === website.websiteCompany.toLowerCase()
      );
      if (alreadyAdded) return false;

      // Only show active websites
      if (!website.isActive) return false;

      // Search filter
      const matchesSearch = !searchQuery ||
        website.websiteCompany.toLowerCase().includes(searchQuery.toLowerCase());

      // Category filter
      const matchesCategory = selectedCategory === 'all' || website.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [allWebsitesWithFlag, searchQuery, selectedCategory, existingWebsites]);

  // Get only selectable (minimal) websites for Select All functionality
  const selectableWebsites = useMemo(() => {
    return filteredWebsites.filter(w => w.isMinimal);
  }, [filteredWebsites]);

  // Group filtered websites by category
  const groupedWebsites = useMemo(() => {
    return WEBSITE_CATEGORIES.reduce((acc, cat) => {
      acc[cat.code] = filteredWebsites.filter(w => w.category === cat.code);
      return acc;
    }, {} as Record<string, WebsiteWithMinimalFlag[]>);
  }, [filteredWebsites]);

  const toggleWebsite = (id: string, isMinimal: boolean) => {
    // Only allow toggling minimal websites (CEO directive)
    if (!isMinimal) return;
    setSelectedWebsites(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const selectAll = () => {
    // Only select minimal websites (CEO directive)
    setSelectedWebsites(new Set(selectableWebsites.map(w => w.id)));
  };

  const selectNone = () => {
    setSelectedWebsites(new Set());
  };

  const selectCategory = (categoryCode: string) => {
    const categoryWebsites = filteredWebsites.filter(w => w.category === categoryCode);
    setSelectedWebsites(prev => {
      const next = new Set(prev);
      categoryWebsites.forEach(w => next.add(w.id));
      return next;
    });
  };

  const handleTemplateSelect = (template: AuditTemplate) => {
    setSelectedTemplate(template);
    setSelectedWebsites(new Set()); // Clear selection when changing template
    setStep(2);
  };

  const handleBack = () => {
    setStep(1);
    setSelectedWebsites(new Set());
  };

  const handleSubmit = async () => {
    if (selectedWebsites.size === 0 || !selectedTemplate) return;

    setIsSubmitting(true);
    try {
      const websitesToAdd = selectableWebsites.filter(w => selectedWebsites.has(w.id));
      await onAddWebsites(websitesToAdd, selectedTemplate.name);
      setSelectedWebsites(new Set());
      // Reset template to current audit's template (if any)
      setSelectedTemplate(currentTemplate ? availableTemplates.find(t => t.name === currentTemplate) || null : null);
      setStep(1);
      onClose();
    } catch (error) {
      console.error('[AddAuditChecklist] Failed to add websites:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset state when modal closes
  const handleClose = () => {
    setStep(1);
    setSelectedTemplate(null);
    setSelectedWebsites(new Set());
    setSearchQuery('');
    setSelectedCategory('all');
    onClose();
  };

  if (!isOpen) return null;

  // CEO directive: All templates now use minimal (77) sites only
  const minimalCount = getMinimalWebsiteCount();

  // Helper to get count and label for template
  // Note: All templates now use minimal list per CEO directive
  const getChecklistInfo = (type: string) => {
    // All templates now show the same minimal count (77 sites)
    const baseColor = type === 'initial_deletion' 
      ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
      : type === 'comprehensive'
        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
        : 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
    
    return { 
      count: minimalCount, // All templates now use minimal list
      label: 'Minimal', 
      color: baseColor 
    };
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Add Audit Checklist</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {step === 1
                ? 'Step 1: Select Audit Type'
                : `Step 2: Select Websites (${selectedWebsites.size} selected)`}
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        {step === 1 ? (
          /* Step 1: Template Selection */
          <div className="overflow-y-auto flex-1 px-6 py-4">
            <div className="mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Select the audit type to determine which websites to include in the checklist.
              </p>
            </div>
            <div className="space-y-2">
              {availableTemplates.map(template => {
                const info = getChecklistInfo(template.checklistType);
                return (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 transition-colors text-left"
                  >
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-gray-100">
                        {template.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Phase 1: {template.phase1DueDateDays} days
                        {template.phase2Required && ` | Phase 2: +${template.phase2DueDateDays} days`}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${info.color}`}>
                      {info.label} ({info.count} sites)
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Step 2: Website Selection */
          <>
            {/* Template Badge & Search/Filters */}
            <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              {/* Selected Template Badge */}
              <div className="flex items-center gap-3 mb-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">Selected:</span>
                <span className="inline-flex items-center gap-2 px-3 py-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg">
                  <span className="font-medium text-gray-900 dark:text-gray-100">{selectedTemplate?.name}</span>
                  {selectedTemplate && (() => {
                    const info = getChecklistInfo(selectedTemplate.checklistType);
                    return (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${info.color}`}>
                        {info.label} ({info.count})
                      </span>
                    );
                  })()}
                </span>
                <button
                  onClick={handleBack}
                  className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                >
                  Change
                </button>
              </div>
              {/* Search and Category Filter */}
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search websites..."
                    className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="all">All Categories</option>
                  {WEBSITE_CATEGORIES.map(cat => (
                    <option key={cat.code} value={cat.code}>{cat.name}</option>
                  ))}
                </select>
                <div className="flex items-center gap-1 text-xs">
                  <button onClick={selectAll} className="px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">Select All</button>
                  <span className="text-gray-300 dark:text-gray-600">|</span>
                  <button onClick={selectNone} className="px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">Clear</button>
                </div>
              </div>
            </div>

            {/* Body - Website List */}
            <div className="overflow-y-auto flex-1 px-6 py-4">
              {filteredWebsites.length === 0 ? (
                <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
                  {existingWebsites.length > 0 && !searchQuery && selectedCategory === 'all'
                    ? 'All available websites have already been added to this audit.'
                    : 'No websites match your search criteria.'}
                </div>
              ) : (
                <div className="space-y-4">
                  {WEBSITE_CATEGORIES.map(category => {
                    const websites = groupedWebsites[category.code];
                    if (!websites || websites.length === 0) return null;

                    return (
                      <div key={category.code} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                        {/* Category Header */}
                        <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                          <div>
                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{category.name}</span>
                            <span className="ml-2 text-xs text-gray-500">({websites.length} available)</span>
                          </div>
                          <button
                            onClick={() => selectCategory(category.code)}
                            className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
                          >
                            Select All
                          </button>
                        </div>
                        {/* Website List */}
                        <div className="divide-y divide-gray-200 dark:divide-gray-700 max-h-48 overflow-y-auto">
                          {websites.map(website => {
                            const isMinimal = (website as WebsiteWithMinimalFlag).isMinimal;
                            return (
                              <label
                                key={website.id}
                                className={`flex items-center px-4 py-2 ${
                                  isMinimal 
                                    ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer' 
                                    : 'opacity-40 cursor-not-allowed bg-gray-100 dark:bg-gray-800'
                                }`}
                                title={isMinimal ? '' : 'Not available - CEO directive: minimal list only'}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedWebsites.has(website.id)}
                                  onChange={() => toggleWebsite(website.id, isMinimal)}
                                  disabled={!isMinimal}
                                  className={`rounded border-gray-300 focus:ring-blue-500 ${
                                    isMinimal ? 'text-blue-600' : 'text-gray-300 cursor-not-allowed'
                                  }`}
                                />
                                <span className={`ml-3 text-sm flex-1 ${
                                  isMinimal 
                                    ? 'text-gray-900 dark:text-gray-100' 
                                    : 'text-gray-400 dark:text-gray-500 line-through'
                                }`}>
                                  {website.websiteCompany}
                                </span>
                                {!isMinimal && (
                                  <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                                    (not in minimal list)
                                  </span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
          <div className="text-sm text-gray-500 dark:text-gray-400 flex flex-col gap-0.5">
            <span>
              {step === 1
                ? `${availableTemplates.length} templates available`
                : `${selectedWebsites.size} website${selectedWebsites.size !== 1 ? 's' : ''} selected`}
            </span>
            {isSubmitting && batchProgress && batchProgress.total > 0 ? (
              <span className="text-blue-600 dark:text-blue-400 font-medium tabular-nums">
                Creating {batchProgress.completed} / {batchProgress.total}…
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {step === 2 && (
              <button
                onClick={handleBack}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                Back
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
            >
              Cancel
            </button>
            {step === 2 && (
              <button
                onClick={handleSubmit}
                disabled={selectedWebsites.size === 0 || isSubmitting}
                className="px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-lg hover:from-blue-700 hover:to-blue-600 shadow-sm active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Adding...' : `Add ${selectedWebsites.size} Website${selectedWebsites.size !== 1 ? 's' : ''}`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const CyberSweepAuditDetail: React.FC<CyberSweepAuditDetailProps> = ({ auditId, onBack }) => {
  const [activeTab, setActiveTab] = useState('related');
  const [selectedPAWRecord, setSelectedPAWRecord] = useState<PAWRecord | null>(null);
  const [websiteInfoName, setWebsiteInfoName] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showNewPAWModal, setShowNewPAWModal] = useState(false);
  const [showAddChecklistModal, setShowAddChecklistModal] = useState(false);
  const [checklistBatchProgress, setChecklistBatchProgress] = useState<{ completed: number; total: number } | null>(
    null
  );
  const [showClientProfileModal, setShowClientProfileModal] = useState(false);
  const [existingChecklistWebsites, setExistingChecklistWebsites] = useState<string[]>([]);

  const onChecklistBatchProgress = useCallback((completed: number, total: number) => {
    setChecklistBatchProgress({ completed, total });
  }, []);

  // Inline editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');

  // Notes state
  const [notes, setNotes] = useState<CSAuditNote[]>([]);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteValue, setEditNoteValue] = useState('');
  const { userName } = useCurrentUser();

  const { data: auditRecord, isLoading: loadingAudit } = useCSAudit(auditId);

  // Fetch users for Auditor dropdown (no filter to get all users)
  const { data: users = [] } = useUsers({});

  // Create auditor dropdown options from users
  const auditorOptions = useMemo(() => {
    return users.map((user: any) => user.name || user.fullName || user.username || String(user.id)).filter(Boolean);
  }, [users]);
  const pawRecordsInfiniteQuery = usePAWRecordsInfinite(auditId);
  const pawRecordCountQuery = usePAWRecordCount(auditId);
  const auditRecordArray = useMemo(() => auditRecord ? [auditRecord as Record<string, unknown>] : [], [auditRecord]);
  const { data: accountNameMap } = useAccountIdToName(auditRecordArray);
  const updateAudit = useUpdateCSAudit();
  const createCSAudit = useCreateCSAudit();
  const createPAWRecord = useCreatePAWRecord();
  const createPAWRecordsBatch = useCreatePAWRecordsBatch(auditId, {
    onBatchProgress: onChecklistBatchProgress,
  });
  const updatePAWRecord = useUpdatePAWRecord();
  const updateClient = useUpdateCyberSweepClient();
  const deleteCSAudit = useDeleteCSAudit();

  // Delete confirmation modal state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Inline editing state for PAW records in checklist (auto-save on blur)
  const [savingRows, setSavingRows] = useState<Set<string>>(new Set());
  const [savedRows, setSavedRows] = useState<Set<string>>(new Set()); // For brief success indicator

  // PAW selection state (for bulk actions)
  const [selectedPAWIds, setSelectedPAWIds] = useState<Set<string>>(new Set());
  const pawRecordsRaw = useMemo(() => {
    const pages = pawRecordsInfiniteQuery.data?.pages ?? [];
    const flattened: Record<string, unknown>[] = [];
    const seen = new Set<string>();
    pages.forEach((page: any) => {
      (page?.records ?? []).forEach((record: Record<string, unknown>) => {
        const dedupeKey = String(record.id ?? record.key ?? '');
        if (!dedupeKey || seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        flattened.push(record);
      });
    });
    return flattened;
  }, [pawRecordsInfiniteQuery.data]);
  const pawRecordsLoading = pawRecordsInfiniteQuery.isLoading;
  const pawRecordsFetchingNextPage = pawRecordsInfiniteQuery.isFetchingNextPage;
  const pawRecordsHasNextPage = Boolean(pawRecordsInfiniteQuery.hasNextPage);
  // Use server count (now fetches all records for accuracy), fallback to fetched count
  const pawRecordCount = pawRecordCountQuery.data ?? pawRecordsRaw.length;

  const refetchVisiblePAWRecords = useCallback(async () => {
    await Promise.all([pawRecordsInfiniteQuery.refetch(), pawRecordCountQuery.refetch()]);
  }, [pawRecordsInfiniteQuery, pawRecordCountQuery]);

  const refetchPAWRecords = useCallback(async () => {
    const [allRecords] = await Promise.all([
      fetchAllPAWRecordsForAudit(auditId),
      refetchVisiblePAWRecords(),
    ]);
    return { data: allRecords };
  }, [auditId, refetchVisiblePAWRecords]);

  const auditMutationTargetId = useMemo(() => {
    const rec = auditRecord as Record<string, unknown> | null;
    return String(rec?.id ?? rec?.key ?? auditId);
  }, [auditRecord, auditId]);

  const buildAuditMetadataTouchUpdates = useCallback(() => {
    const now = new Date().toISOString();
    return {
      LastModifiedBy__c: userName || 'System',
      lastModifiedBy: userName || 'System',
      LastModifiedDate__c: now,
      lastModifiedDate: now,
    };
  }, [userName]);

  // Handler for saving audit field changes
  const handleSaveAudit = useCallback(async (updates: Record<string, unknown>) => {
    if (!auditMutationTargetId) return;

    try {
      // Add Last Modified fields
      const updatesWithMeta = {
        ...updates,
        ...buildAuditMetadataTouchUpdates(),
      };

      await updateAudit.mutateAsync({
        id: auditMutationTargetId,
        updates: updatesWithMeta,
        detailAuditId: auditId,
      });
      console.log('[handleSaveAudit] Saved updates:', Object.keys(updates));
    } catch (error) {
      console.error('[handleSaveAudit] Error saving audit:', error);
    }
  }, [auditId, auditMutationTargetId, buildAuditMetadataTouchUpdates, updateAudit]);

  const touchAuditMetadataFromChecklistEdit = useCallback(async () => {
    if (!auditMutationTargetId) return;
    await updateAudit.mutateAsync({
      id: auditMutationTargetId,
      updates: buildAuditMetadataTouchUpdates(),
      detailAuditId: auditId,
    });
  }, [auditId, auditMutationTargetId, buildAuditMetadataTouchUpdates, updateAudit]);

  // ─── Business Logic Adapter Functions ─────────────────────────────────────
  // These adapt the Gobodo service calls to match the business logic interface

  const getAuditByIdForWorkflow = async (id: string): Promise<BusinessLogicCSAuditRecord> => {
    console.log('=== getAuditByIdForWorkflow CALLED ===');
    console.log('Requested Audit ID:', id);

    const record = await getGobodoRecordById(id);
    console.log('>>> RAW Gobodo record:', JSON.stringify(record, null, 2));

    if (!record) throw new Error(`Audit ${id} not found`);
    const data = (record as any).data ?? record;
    console.log('>>> Extracted data object:', JSON.stringify(data, null, 2));

    // DEBUG: Log all template-related fields
    console.log('=== getAuditByIdForWorkflow DEBUG ===');
    console.log('Audit ID:', id);
    console.log('data.Audit_Template__c:', data.Audit_Template__c);
    console.log('data.Audit_Type_Name__c:', data.Audit_Type_Name__c);
    console.log('data.auditTemplate:', data.auditTemplate);
    console.log('data.auditTypeName:', data.auditTypeName);
    console.log('data.auditType:', data.auditType);
    console.log('data.Phase_Status__c:', data.Phase_Status__c);
    console.log('data.phaseStatus:', data.phaseStatus);

    // Check Audit_Type_Name__c FIRST - that's where the human-readable template name is stored
    // e.g., "Initial Deletion", "Initial Audit", "Final Audit", "Quarter #1 Audit"
    const templateName = data.Audit_Type_Name__c ?? data.auditTypeName ?? data.Audit_Template__c ?? data.auditTemplate ?? data.auditType ?? '';

    console.log('Resolved templateName:', templateName);

    // Capture all possible account ID field names
    const accountIdValue = data.Account__c ?? data.Custom__account__c ?? data.custom__account ??
                          data.accountId ?? data.AccountId ?? data.account_id ?? '';

    console.log('Account ID field values:', {
      'data.Account__c': data.Account__c,
      'data.Custom__account__c': data.Custom__account__c,
      'data.custom__account': data.custom__account,
      'data.accountId': data.accountId,
      'Resolved accountId': accountIdValue,
    });

    const result = {
      id: String(record.id ?? id),
      Audit_Template__c: String(templateName),
      Phase_Status__c: String(data.Phase_Status__c ?? data.phaseStatus ?? 'Phase 1 - Not Started'),
      Phase_2_Required__c: Boolean(data.Phase_2_Required__c ?? data.phase2Required ?? false),
      Account__c: String(accountIdValue),
      accountId: String(accountIdValue),
    };

    console.log('Mapped audit for workflow:', result);
    return result;
  };

  const getAccountByIdForWorkflow = async (accountId: string): Promise<BusinessLogicAccountRecord> => {
    console.log('=== getAccountByIdForWorkflow (SIMPLE) ===');
    console.log('Account ID:', accountId);

    const readAccountStatus = (record: Record<string, unknown> | null) => {
      const rec = record ?? {};
      const data = (rec.data as Record<string, unknown>) ?? {};
      const rawStatus =
        g(rec, 'CS_Account_Status__c', 'Cyber_Sweep_Status__c', 'Custom__cyber_sweep_status__c', 'cyberSweepStatus') ??
        g(data, 'CS_Account_Status__c', 'Cyber_Sweep_Status__c', 'Custom__cyber_sweep_status__c', 'cyberSweepStatus');
      return rawStatus != null ? String(rawStatus) : '';
    };

    const readAccountName = (record: Record<string, unknown> | null) => {
      const rec = record ?? {};
      const data = (rec.data as Record<string, unknown>) ?? {};
      const rawName =
        g(rec, 'Name__c', 'Name', 'name', 'label') ??
        g(data, 'Name__c', 'Name', 'name', 'label');
      return rawName != null ? String(rawName) : '';
    };

    const fallbackRecord = (clientRecord as Record<string, unknown> | null) ?? null;
    const record = (/^\d+$/.test(accountId)
      ? await getGobodoRecordById(accountId)
      : await getGobodoRecordByKey(accountId)) ?? fallbackRecord;

    return {
      id: accountId,
      Name: readAccountName(record) || auditData?.clientName || auditData?.ClientName__c || auditData?.AccountName__c || 'Unknown',
      CS_Account_Status__c: readAccountStatus(record),
    };
  };

  const updateAuditRecordForWorkflow = async (id: string, updates: Partial<BusinessLogicCSAuditRecord>): Promise<void> => {
    const mappedUpdates: Record<string, unknown> = {
      ...updates,
      ...buildAuditMetadataTouchUpdates(),
    };

    if (typeof updates.Phase_Status__c === 'string' && !mappedUpdates.phaseStatus) {
      mappedUpdates.phaseStatus = updates.Phase_Status__c;
    }
    if (typeof updates.phaseStatus === 'string' && !mappedUpdates.Phase_Status__c) {
      mappedUpdates.Phase_Status__c = updates.phaseStatus;
    }
    if (typeof updates.Phase_1_Start_Date_Time__c === 'string' && !mappedUpdates.phase1StartDateTime) {
      mappedUpdates.phase1StartDateTime = updates.Phase_1_Start_Date_Time__c;
    }
    if (typeof updates.Phase_2_Start_Date_Time__c === 'string' && !mappedUpdates.phase2StartDateTime) {
      mappedUpdates.phase2StartDateTime = updates.Phase_2_Start_Date_Time__c;
    }
    if (typeof updates.Phase_1_Completed_Date_Time__c === 'string' && !mappedUpdates.phase1CompletedDateTime) {
      mappedUpdates.phase1CompletedDateTime = updates.Phase_1_Completed_Date_Time__c;
    }
    if (typeof updates.Phase_2_Completed_Date_Time__c === 'string' && !mappedUpdates.phase2CompletedDateTime) {
      mappedUpdates.phase2CompletedDateTime = updates.Phase_2_Completed_Date_Time__c;
    }

    await updateAudit.mutateAsync({
      id,
      updates: mappedUpdates,
      detailAuditId: auditId,
    });
  };

  const updatePAWRecordForWorkflow = async (id: string, updates: Partial<BusinessLogicPAWRecord>): Promise<void> => {
    await updatePAWRecord.mutateAsync({ id, updates: updates as Record<string, unknown> });
  };

  const updateAccountRecordForWorkflow = async (accountId: string, data: Partial<BusinessLogicAccountRecord>): Promise<void> => {
    console.log('=== updateAccountRecordForWorkflow ===');
    console.log('Updating account:', accountId);
    console.log('Data:', data);

    // Map CS_Account_Status__c to the ACTUAL Gobodo field names
    const mappedData: Record<string, unknown> = {};

    if (data.CS_Account_Status__c) {
      // Save to ALL possible field name variants
      mappedData.Custom__cyber_sweep_status__c = data.CS_Account_Status__c;
      mappedData.Cyber_Sweep_Status__c = data.CS_Account_Status__c;
      mappedData.CS_Account_Status__c = data.CS_Account_Status__c;
      mappedData.cyberSweepStatus = data.CS_Account_Status__c;
    }

    console.log('Mapped data for Gobodo:', mappedData);
    if (Object.keys(mappedData).length === 0) return;
    const clientMutationTargetId = String((clientRecord as Record<string, unknown> | null)?.id ?? accountId);
    await updateClient.mutateAsync({
      id: clientMutationTargetId,
      updates: mappedData,
      detailClientId: auditData?.clientId || accountId,
    });
    console.log('Account update complete');
  };

  const createCSAuditRecordForWorkflow = async (params: CreateCSAuditParams): Promise<{ id: string }> => {
    console.log('=== createCSAuditRecordForWorkflow ===');
    console.log('Received params:', params);

    // Generate CyberAudit-XXXXX name client-side (Gobodo doesn't auto-generate)
    // This matches how manual CS Audit creation works in CreateCSAuditModal
    const generateAuditName = () => {
      const num = Math.floor(10000 + Math.random() * 90000);
      return `CyberAudit-${num}`;
    };
    const auditName = generateAuditName();

    // Use current fiscal year (not derived from due date)
    const fiscalYear = new Date().getFullYear();

    const now = new Date().toISOString();
    const newAuditData = {
      Account__c: params.accountId,
      custom__account: params.accountId,
      // Set Name__c explicitly - Gobodo uses this as the record name/label
      Name__c: auditName,
      name: auditName,
      Audit_Template__c: params.templateName,
      Audit_Type_Name__c: params.templateName,
      auditTemplate: params.templateName,
      auditTypeName: params.templateName,
      Phase_1_Due_Date__c: params.phase1DueDate,
      phase1DueDate: params.phase1DueDate,
      // Overall due date: phase1DueDate if no Phase 2, else phase2DueDate
      Due_Date__c: params.dueDate,
      dueDate: params.dueDate,
      Phase_2_Due_Date__c: params.phase2DueDate,
      phase2DueDate: params.phase2DueDate,
      Phase_2_Required__c: params.phase2Required,
      phase2Required: params.phase2Required,
      Phase_Status__c: 'Phase 1 - Not Started',
      phaseStatus: 'Phase 1 - Not Started',
      // Set Phase 1 Start Date/Time when audit is created
      Phase_1_Start_Date_Time__c: now,
      phase1StartDateTime: now,
      Fiscal_Year__c: fiscalYear,
      fiscalYear: fiscalYear,
      CreatedDate__c: now,
      createdDate: now,
    };

    console.log('Creating CS Audit with data:', newAuditData);

    try {
      const result = await createCSAudit.mutateAsync(newAuditData);
      console.log('>>> createCSAudit SUCCESS:', result);
      return { id: String(result.id ?? (result as any).key ?? '') };
    } catch (error) {
      console.error('>>> createCSAudit FAILED:', error);
      throw error;
    }
  };

  const findExistingNextAuditForWorkflow = async (
    accountId: string,
    templateName: string
  ): Promise<{ id: string } | null> => {
    const audits = await fetchCSAuditsForClientKeysMerged([accountId]);
    const matchingAudit = audits.find((raw) => {
      const record = raw as Record<string, unknown>;
      const data = (record.data as Record<string, unknown>) ?? {};
      const rawTemplate =
        data.Audit_Type_Name__c ??
        data.auditTypeName ??
        data.Audit_Template__c ??
        data.auditTemplate ??
        record.Audit_Type_Name__c ??
        record.auditTypeName ??
        record.Audit_Template__c ??
        record.auditTemplate;
      const rawPhaseStatus =
        data.Phase_Status__c ??
        data.phaseStatus ??
        record.Phase_Status__c ??
        record.phaseStatus;
      return (
        String(rawTemplate ?? '').trim() === templateName &&
        String(rawPhaseStatus ?? '').trim() !== 'Audit Complete'
      );
    });

    if (!matchingAudit) return null;
    return { id: String((matchingAudit as Record<string, unknown>).id ?? '') };
  };

  // Build user ID to name map for resolving createdBy/lastModifiedBy
  const userIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    users.forEach(user => {
      const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || (user as any).name || '';
      if (!name) return;

      // Map user.id
      if (user.id) {
        map[user.id] = name;
      }

      // Map user.key (Gobodo graph key)
      const userKey = (user as Record<string, unknown>).key;
      if (userKey && userKey !== user.id) {
        map[String(userKey)] = name;
      }

      // Map keycloakUserId (numeric IDs like "37876", "1543")
      if (user.keycloakUserId) {
        map[user.keycloakUserId] = name;
      }

      // Map graphObjectKey
      if (user.graphObjectKey && user.graphObjectKey !== user.id) {
        map[user.graphObjectKey] = name;
      }
    });
    return map;
  }, [users]);

  const auditData = useMemo(() => mapRecordToAuditData(auditRecord as Record<string, unknown> | null, accountNameMap), [auditRecord, accountNameMap]);

  // Fetch client/account record for profile modal
  const { data: clientRecord } = useCyberSweepClient(auditData?.clientId || null);

  // Map client record to profile data for the modal
  const clientProfileData: ClientProfileData = useMemo(() => {
    const rec = (clientRecord as Record<string, unknown> | null) ?? {};
    const data = (rec.data as Record<string, unknown>) ?? (clientRecord as Record<string, unknown> | undefined) ?? {};
    const g = (obj: Record<string, unknown>, ...keys: string[]): unknown => {
      for (const k of keys) {
        if (obj[k] !== undefined && obj[k] !== null) return obj[k];
      }
      return undefined;
    };
    const gAll = (...keys: string[]): unknown => g(rec, ...keys) ?? g(data, ...keys);
    const fmt = (v: unknown) => (v != null && v !== '' ? String(v) : undefined);
    const suffix = getCustomSuffix();

    // Collect emails - check multiple potential field names
    const emails: string[] = [];
    const primaryEmail = fmt(
      gAll(
        'personalEmailAddress',
        'PersonEmail',
        'PersonEmail__c',
        `PersonEmail${suffix}`,
        'PersonEmail__stg3_c',
        'PersonEmail__stg5_c',
        'personemail',
        'email',
        'Email__c',
        'PersonalEmailAddress__c',
        'custom__person_email'
      )
    );
    if (primaryEmail) emails.push(primaryEmail);
    const secondaryEmail = fmt(g(data, 'secondaryEmail', 'Secondary_Email__c'));
    if (secondaryEmail) emails.push(secondaryEmail);

    // Collect phones (root or nested `data`, like email)
    const phones: string[] = [];
    const primaryPhone = fmt(
      gAll('clientPersonalPhone', 'Client_Personal_Phone__c', 'phone', 'Phone__c', 'PersonPhone__c', 'PersonMobilePhone')
    );
    if (primaryPhone) phones.push(primaryPhone);
    const mobilePhone = fmt(gAll('mobilePhone', 'Mobile_Phone__c', 'MobilePhone'));
    if (mobilePhone && mobilePhone !== primaryPhone) phones.push(mobilePhone);

    // Previous addresses
    const previousAddresses: string[] = [];
    const prevAddr = fmt(g(data, 'previousHomeAddress', 'Previous_Home_Address__c', 'PreviousAddress__c'));
    if (prevAddr) previousAddresses.push(prevAddr);

    // Aliases
    const aliases: string[] = [];
    const aliasStr = fmt(g(data, 'aliases', 'Aliases__c', 'Other_Names__c', 'AKA__c'));
    if (aliasStr) {
      // Split by comma or semicolon
      aliases.push(...aliasStr.split(/[,;]/).map(s => s.trim()).filter(Boolean));
    }

    // Display name: prefer **Account** fields (where phone/email are sourced), then audit traversal / map, then audit string, then a contact hint
    const rawAccountName = (() => {
      const v = gAll('clientName', 'ClientName__c', 'Name__c', 'Name', 'AccountName__c', 'name');
      return v != null && v !== '' ? String(v).trim() : '';
    })();
    const fPart = gAll('firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name');
    const lPart = gAll('lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name');
    const nameFromParts = [fPart, lPart]
      .map((x) => (x != null && x !== '' ? String(x).trim() : ''))
      .filter(Boolean)
      .join(' ')
      .trim();
    const fromClient =
      (rawAccountName && !isGobodoLabel(rawAccountName) ? rawAccountName : '') ||
      (nameFromParts && !isGobodoLabel(nameFromParts) ? nameFromParts : '');

    const travName = auditRecord
      ? readCSAuditClientNameFromClientTraversal(auditRecord as Record<string, unknown>, suffix)
      : '';
    const mapName =
      auditData?.clientId && accountNameMap
        ? lookupAccountDisplayName(accountNameMap, auditData.clientId) || ''
        : '';
    const auditNm = auditData?.clientName;
    const auditNameOk =
      auditNm && auditNm !== 'Unknown' && auditNm !== 'Unknown Client' && !isGobodoLabel(auditNm) ? auditNm : '';

    const name =
      fromClient ||
      travName ||
      (mapName && !isGobodoLabel(String(mapName)) ? mapName : '') ||
      auditNameOk ||
      (emails[0] || '') ||
      (phones[0] || '') ||
      'Unknown Client';

    return {
      id: auditData?.clientId || '',
      name,
      firstName: fmt(gAll('firstName', 'FirstName__c', 'First_Name__c', 'custom__first_name')),
      lastName: fmt(gAll('lastName', 'LastName__c', 'Last_Name__c', 'custom__last_name')),
      dateOfBirth: fmt(gAll('dateOfBirth', 'Date_of_Birth__c', 'DOB__c', 'Birthdate', 'PersonBirthdate')),
      currentAddress: fmt(
        gAll('currentHomeAddress', 'Current_Home_Address__c', 'BillingStreet', 'PersonMailingStreet', 'Address__c')
      ),
      previousAddresses: previousAddresses.length > 0 ? previousAddresses : undefined,
      aliases: aliases.length > 0 ? aliases : undefined,
      emails: emails.length > 0 ? emails : undefined,
      phones: phones.length > 0 ? phones : undefined,
      ssnLast4: fmt(
        gAll('ssnLast4', 'Last_4_SSN__c', 'SSN_Last_4__c', 'SSNLast4__c', 'custom__last_4_ssn', 'custom__last_four_ssn')
      ),
    };
  }, [clientRecord, auditData, accountNameMap, auditRecord]);

  // Resolve auditor ID to name
  const auditorDisplayName = useMemo(() => {
    if (!auditData?.auditor) return '—';
    // Check if it's already a name (not an ID)
    const auditorValue = auditData.auditor;
    // If it looks like an ID (numeric or alphanumeric), try to resolve it
    if (/^\d+$/.test(auditorValue) || /^[a-zA-Z0-9]{15,18}$/.test(auditorValue)) {
      const resolvedName = userIdToNameMap[auditorValue];
      if (resolvedName) return resolvedName;
    }
    return auditorValue;
  }, [auditData?.auditor, userIdToNameMap]);

  // Resolve audit type - filter out CyberAudit-XXXXX / CSA-XXXXX patterns (that's the audit name, not type)
  const auditTypeDisplayName = useMemo(() => {
    if (!auditData?.auditType) return '—';
    const auditType = auditData.auditType;
    // Filter out CyberAudit-XXXXX, CSA-XXXXX patterns and SF IDs
    if (/^(CyberAudit|CSA)-\d+$/i.test(auditType)) return '—';
    if (/^[a-zA-Z0-9]{15,18}$/.test(auditType) && /^[a-z][0-9][A-Za-z]/.test(auditType)) return '—';
    return auditType;
  }, [auditData?.auditType]);

  // Resolve client name for Account field
  const accountDisplayName = useMemo(() => {
    if (!auditData) return '—';

    // Debug: log all possible sources
    if (process.env.NODE_ENV === 'development') {
      console.log('[Account Resolution] auditData.clientName:', auditData.clientName);
      console.log('[Account Resolution] auditData.clientId:', auditData.clientId);
      console.log('[Account Resolution] accountNameMap:', accountNameMap);
    }

    // Try multiple sources in order of preference:

    // 1. Direct lookup from accountNameMap using clientId
    if (auditData.clientId && accountNameMap) {
      // Try exact match
      if (accountNameMap[auditData.clientId]) {
        const name = accountNameMap[auditData.clientId];
        if (!isGobodoLabel(name)) {
          console.log('[Account Resolution] Found via exact lookup:', name);
          return name;
        }
      }
      // Try with suffix stripped
      const stripped = auditData.clientId.replace(/(__stg5_c|__stg3_c|__stg_c|__dev_c|__c)$/i, '');
      if (accountNameMap[stripped]) {
        const name = accountNameMap[stripped];
        if (!isGobodoLabel(name)) {
          console.log('[Account Resolution] Found via stripped lookup:', name);
          return name;
        }
      }
      // Try all keys in map that might match
      for (const [key, name] of Object.entries(accountNameMap)) {
        if (key.includes(stripped) || stripped.includes(key.replace(/(__stg5_c|__stg3_c|__stg_c|__dev_c|__c)$/i, ''))) {
          if (!isGobodoLabel(name)) {
            console.log('[Account Resolution] Found via partial match:', name);
            return name;
          }
        }
      }
    }

    // 2. Use clientName if it's a valid name (not auto-generated)
    if (auditData.clientName && auditData.clientName !== 'Unknown' && !isGobodoLabel(auditData.clientName)) {
      console.log('[Account Resolution] Using clientName:', auditData.clientName);
      return auditData.clientName;
    }

    // 3. Check raw audit record for any name fields we might have missed
    if (auditRecord) {
      const raw = auditRecord as Record<string, unknown>;
      const data = (raw.data as Record<string, unknown>) ?? raw;
      const possibleNames = [
        data.AccountName__c,
        data.Account_Name__c,
        data.ClientName__c,
        data.Client_Name__c,
        raw.AccountName__c,
        raw.Account_Name__c,
        raw.ClientName__c,
        raw.Client_Name__c,
      ].filter(Boolean).map(String);

      for (const name of possibleNames) {
        if (name && !isGobodoLabel(name) && name !== 'Unknown') {
          console.log('[Account Resolution] Found in raw record:', name);
          return name;
        }
      }
    }

    console.log('[Account Resolution] No valid name found, returning —');
    return '—';
  }, [auditData, accountNameMap, auditRecord]);

  // Debug logging for field resolution
  if (process.env.NODE_ENV === 'development' && auditData) {
    console.group('[CSAudit Details] Field Resolution Debug');
    console.log('Full audit record:', auditRecord);
    console.log('auditData.clientId (Account__c):', auditData.clientId);
    console.log('auditData.clientName:', auditData.clientName);
    console.log('isGobodoLabel(clientName):', auditData.clientName ? isGobodoLabel(auditData.clientName) : 'N/A');
    console.log('accountNameMap:', accountNameMap);
    console.log('accountNameMap keys:', accountNameMap ? Object.keys(accountNameMap) : 'undefined');
    console.log('→ Final accountDisplayName:', accountDisplayName);
    console.log('auditData.auditor:', auditData.auditor, '→ resolved:', auditorDisplayName);
    console.log('auditData.auditType:', auditData.auditType, '→ resolved:', auditTypeDisplayName);
    console.log('userIdToNameMap keys:', Object.keys(userIdToNameMap).slice(0, 10));
    console.groupEnd();
  }

  // Map PAW records and resolve user IDs to names
  const pawRecords = useMemo(() => {
    return (pawRecordsRaw as Record<string, unknown>[]).map(r => {
      const mapped = mapToPAWRecord(r);
      // Try to resolve createdBy - check both Gobodo numeric IDs and SF IDs
      if (mapped.createdBy) {
        const resolvedName = userIdToNameMap[mapped.createdBy];
        if (resolvedName) {
          mapped.createdBy = resolvedName;
        }
        // If still looks like an ID (numeric or SF format), it wasn't resolved - leave as-is for now
      }
      // Try to resolve lastModifiedBy - check both Gobodo numeric IDs and SF IDs
      if (mapped.lastModifiedBy) {
        const resolvedName = userIdToNameMap[mapped.lastModifiedBy];
        if (resolvedName) {
          mapped.lastModifiedBy = resolvedName;
        }
      }
      return mapped;
    });
  }, [pawRecordsRaw, userIdToNameMap]);

  // Sort PAW records: empty findings first (to work on), completed findings last
  const sortedPawRecords = useMemo(() => {
    return [...pawRecords].sort((a, b) => {
      const aHasFindings = a.findings && a.findings.trim() !== '' && a.findings !== '-- Select --';
      const bHasFindings = b.findings && b.findings.trim() !== '' && b.findings !== '-- Select --';
      if (aHasFindings && !bHasFindings) return 1;  // a has findings, goes to bottom
      if (!aHasFindings && bHasFindings) return -1; // b has findings, goes to bottom
      return 0; // maintain relative order
    });
  }, [pawRecords]);

  const relatedPawRecords = pawRecords;

  // Select All handler for PAW records
  const handleSelectAllPAW = useCallback((checked: boolean) => {
    if (checked) {
      setSelectedPAWIds(new Set(pawRecords.map(p => p.id)));
    } else {
      setSelectedPAWIds(new Set());
    }
  }, [pawRecords]);

  // Toggle single PAW selection
  const handleTogglePAWSelection = useCallback((pawId: string) => {
    setSelectedPAWIds(prev => {
      const next = new Set(prev);
      if (next.has(pawId)) {
        next.delete(pawId);
      } else {
        next.add(pawId);
      }
      return next;
    });
  }, []);

  // Inline editing handlers
  const handleFieldEdit = (fieldKey: string, currentValue: string) => {
    setEditingField(fieldKey);
    setTempValue(currentValue || '');
  };

  const handleFieldSave = (fieldKey: string) => {
    if (auditData) {
      const updates: Record<string, unknown> = { [fieldKey]: tempValue };

      // Auto-stamp dates when phase status changes
      if (fieldKey === 'phaseStatus') {
        const previousStatus = auditData.phaseStatus || '';
        const newStatus = tempValue;
        const now = new Date().toISOString();

        // Phase 1 - In Progress: stamp phase1StartDateTime (only if not already started)
        if ((newStatus === 'P1 - In Progress' || newStatus === 'Phase 1 - In Progress') &&
            previousStatus !== 'P1 - In Progress' && previousStatus !== 'Phase 1 - In Progress') {
          if (!auditData.phase1StartDateTime) {
            updates.phase1StartDateTime = now;
            console.log('[DateStamp] Phase 1 Started:', now);
          }
        }
        // Phase 1 - Completed: stamp phase1CompletedDateTime
        else if ((newStatus === 'P1 - Completed' || newStatus === 'Phase 1 - Completed') &&
                 previousStatus !== 'P1 - Completed' && previousStatus !== 'Phase 1 - Completed') {
          updates.phase1CompletedDateTime = now;
          console.log('[DateStamp] Phase 1 Completed:', now);
        }
        // Phase 2 - In Progress: stamp phase2StartDateTime (only if not already started)
        else if ((newStatus === 'P2 - In Progress' || newStatus === 'Phase 2 - In Progress') &&
                 previousStatus !== 'P2 - In Progress' && previousStatus !== 'Phase 2 - In Progress') {
          if (!auditData.phase2StartDateTime) {
            updates.phase2StartDateTime = now;
            console.log('[DateStamp] Phase 2 Started:', now);
          }
        }
        // Audit Completed: stamp phase2CompletedDateTime (and phase1CompletedDateTime if not already set)
        else if ((newStatus === 'Audit Completed' || newStatus === 'Audit Complete') &&
                 previousStatus !== 'Audit Completed' && previousStatus !== 'Audit Complete') {
          // If Phase 1 was never explicitly completed, stamp it now
          if (!auditData.phase1CompletedDateTime) {
            updates.phase1CompletedDateTime = now;
            console.log('[DateStamp] Phase 1 Completed (auto-stamped on Audit Complete):', now);
          }
          updates.phase2CompletedDateTime = now;
          console.log('[DateStamp] Audit Completed:', now);
        }
      }

      handleSaveAudit(updates);
    }
    setEditingField(null);
    setTempValue('');
  };

  const handleFieldCancel = () => {
    setEditingField(null);
    setTempValue('');
  };

  // Note handlers
  const handleAddNote = () => {
    if (!newNote.trim()) return;
    const note: CSAuditNote = {
      id: `note-${Date.now()}`,
      body: newNote.trim(),
      createdBy: userName,
      createdDate: new Date().toISOString(),
    };
    setNotes(prev => [note, ...prev]);
    setNewNote('');
    setIsAddingNote(false);
    console.log('[CS Audit Notes] Added note:', note);
  };

  const handleStartEditNote = (note: CSAuditNote) => {
    setEditingNoteId(note.id);
    setEditNoteValue(note.body);
  };

  const handleSaveEditedNote = () => {
    if (!editingNoteId || !editNoteValue.trim()) return;
    setNotes(prev => prev.map(note =>
      note.id === editingNoteId
        ? { ...note, body: editNoteValue.trim(), lastModifiedBy: userName, lastModifiedDate: new Date().toISOString() }
        : note
    ));
    setEditingNoteId(null);
    setEditNoteValue('');
    console.log('[CS Audit Notes] Updated note:', editingNoteId);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditNoteValue('');
  };

  // Format date for display (use undefined locale for user's local timezone)
  const formatNoteDate = (dateStr: string): string => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  const roadBlockDisplay =
    (auditData?.roadBlockNote || auditData?.roadblockNotes || '').trim() || '—';

  // Helper to resolve user ID/UUID to display name
  const resolveUserDisplayName = (value: string | undefined | null): string => {
    if (!value) return '—';
    const trimmed = String(value).trim();
    if (!trimmed) return '—';

    // Check if it's in the user map (by any ID type)
    if (userIdToNameMap[trimmed]) {
      return userIdToNameMap[trimmed];
    }

    // If it's a numeric ID that couldn't be resolved
    if (/^\d+$/.test(trimmed)) {
      return `User ${trimmed}`;
    }

    // If it's a UUID that couldn't be resolved
    if (/^[a-f0-9-]{36}$/i.test(trimmed)) {
      return 'Unknown User';
    }

    // Otherwise it's probably already a name
    return trimmed;
  };

  // Helper to format date with time (use undefined locale for user's local timezone)
  // Normalizes dates for consistent parsing
  const formatDateTimeDisplay = (value: string | undefined | null): string => {
    if (!value) return '—';
    const trimmed = String(value).trim();
    if (!trimmed) return '—';
    try {
      // Normalize date strings for consistent parsing:
      // 1. Date-only (YYYY-MM-DD): Treat as local midnight, not UTC
      // 2. DateTime without Z (YYYY-MM-DDTHH:MM): Append 'Z' to treat as UTC
      let dateStr = trimmed;

      // Check if it's a date-only string (no T)
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        // Date-only: add T00:00:00 to parse as local time (not UTC)
        dateStr = trimmed + 'T00:00:00';
      } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(trimmed) && !trimmed.endsWith('Z') && !trimmed.includes('+') && !trimmed.includes('-', 10)) {
        // DateTime without timezone: append 'Z' to treat as UTC
        dateStr = trimmed + 'Z';
      }

      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return trimmed;
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return trimmed;
    }
  };

  // Separate display values for System Information
  const createdByDisplay = resolveUserDisplayName(auditData?.createdBy);
  const createdDateDisplay = formatDateTimeDisplay(auditData?.createdDate);
  const lastModifiedByDisplay = resolveUserDisplayName(auditData?.lastModifiedBy);
  const lastModifiedDateDisplay = formatDateTimeDisplay(auditData?.lastModifiedDate || auditData?.lastModified);

  // Read-only field row (no edit icon)
  const sfReadOnlyRow = (label: string, value: React.ReactNode) => (
    <div key={label} className="min-w-0 sm:col-span-1">
      <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
      <div className="flex items-center">
        <span className="text-sm text-gray-900 dark:text-gray-100 break-words">{value || '—'}</span>
      </div>
    </div>
  );

  // Inline editable field row with dropdown
  const sfEditableSelectRow = (label: string, fieldKey: string, value: string, options: string[]) => {
    const isEditing = editingField === fieldKey;
    return (
      <div key={label} className="min-w-0 sm:col-span-1">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <select
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              autoFocus
            >
              <option value="">Select {label}</option>
              {options.map((opt) => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            <button
              onClick={() => handleFieldSave(fieldKey)}
              className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
              title="Save"
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              onClick={handleFieldCancel}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between group">
            <span className="text-sm text-gray-900 dark:text-gray-100 break-words">{value || '—'}</span>
            <button
              onClick={() => handleFieldEdit(fieldKey, value)}
              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-opacity"
              title={`Edit ${label}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Inline editable text field row
  const sfEditableTextRow = (label: string, fieldKey: string, value: string) => {
    const isEditing = editingField === fieldKey;
    return (
      <div key={label} className="min-w-0 sm:col-span-1">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              onClick={() => handleFieldSave(fieldKey)}
              className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
              title="Save"
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              onClick={handleFieldCancel}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between group">
            <span className="text-sm text-gray-900 dark:text-gray-100 break-words">{value || '—'}</span>
            <button
              onClick={() => handleFieldEdit(fieldKey, value)}
              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-opacity"
              title={`Edit ${label}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Inline editable date field row
  const sfEditableDateRow = (label: string, fieldKey: string, value: string, displayValue: string) => {
    const isEditing = editingField === fieldKey;
    return (
      <div key={label} className="min-w-0 sm:col-span-1">
        <div className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</div>
        {isEditing ? (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
            <button
              onClick={() => handleFieldSave(fieldKey)}
              className="p-1 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
              title="Save"
            >
              <Save className="h-4 w-4" />
            </button>
            <button
              onClick={handleFieldCancel}
              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
              title="Cancel"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between group">
            <span className="text-sm text-gray-900 dark:text-gray-100 break-words">{displayValue || '—'}</span>
            <button
              onClick={() => handleFieldEdit(fieldKey, value)}
              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-opacity"
              title={`Edit ${label}`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>({});
  const toggleSection = (title: string) =>
    setCollapsedSections((prev) => ({ ...prev, [title]: !prev[title] }));

  const sfSection = (title: string, rows: React.ReactNode[]) => {
    const isCollapsed = collapsedSections[title] ?? false;
    return (
      <div
        key={title}
        className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
      >
        <button
          onClick={() => toggleSection(title)}
          className="flex items-center gap-2 w-full px-4 py-2.5 text-left bg-gray-50 dark:bg-gray-900/40"
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4 text-gray-500" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-500" />
          )}
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</span>
        </button>
        {!isCollapsed && (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">{rows}</div>
        )}
      </div>
    );
  };

  const renderPAWWebsiteValue = (paw: PAWRecord, clickableForDetail = false) => {
    const displayName = paw.websiteDisplayName || paw.website || '—';
    // NOTE: URLs (client info links) are now displayed in the Link(s) column, not here

    const infoButton = displayName !== '—' && (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setWebsiteInfoName(displayName);
        }}
        className="ml-1 p-0.5 text-gray-400 hover:text-blue-500 rounded transition-colors"
        title="View website opt-out info"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
    );

    const content = (
      <div className="min-w-0 flex items-center">
        <div className="truncate">{displayName}</div>
        {infoButton}
      </div>
    );

    if (!clickableForDetail) return content;

    return (
      <div className="flex items-center">
        <div
          className="text-blue-600 cursor-pointer hover:underline truncate"
          onClick={() => setSelectedPAWRecord(paw)}
        >
          {displayName}
        </div>
        {infoButton}
      </div>
    );
  };

  // Auto-save handler for PAW records in Audit Checklist (Phase 3)
  const handlePAWFieldChange = async (
    pawId: string,
    field: 'findings' | 'piiFound' | 'websiteUrl' | 'clientInfoLinks',
    value: string | string[]
  ) => {
    console.group(`[PAW Save] handlePAWFieldChange START`);
    console.log('PAW ID:', pawId);
    console.log('Field:', field);
    console.log('Value:', value);
    console.log('Timestamp:', new Date().toISOString());

    // Add to saving indicator
    console.log('[PAW Save] Setting saving state...');
    setSavingRows(prev => {
      console.log('[PAW Save] Previous saving rows:', Array.from(prev));
      const next = new Set(prev).add(pawId);
      console.log('[PAW Save] New saving rows:', Array.from(next));
      return next;
    });

    try {
      let updates: Record<string, unknown> = {
        LastModifiedDate__c: new Date().toISOString(),
        LastModifiedBy__c: userName || 'Unknown User',
      };

      if (field === 'findings') {
        // Save to ALL possible field name variants to ensure Gobodo picks it up
        updates.Findings__c = value;
        updates.findings = value;
        updates.Custom__findings__c = value;
        updates.custom__findings = value;
        // Also try the environment-specific suffix if present
        const customSuffix = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
        if (customSuffix !== '__c') {
          updates[`Findings${customSuffix}`] = value;
          updates[`Custom__findings${customSuffix}`] = value;
        }
        console.log('[PAW Save] Findings field updates:', { value, fieldVariants: Object.keys(updates).filter(k => k.toLowerCase().includes('finding')) });
      } else if (field === 'piiFound') {
        const normalizedValues = Array.isArray(value) ? value : parsePiiFoundValue(value);
        updates.PII_Found__c = normalizedValues;
        updates.piiFound = serializePiiFoundValue(normalizedValues);
      } else if (field === 'websiteUrl') {
        // Website URL field (opt-out URL, etc.)
        updates.Website_URL__c = value;
        updates.Opt_Out_URL__c = value;
        updates.websiteUrl = value;
      } else if (field === 'clientInfoLinks') {
        // Link(s) for client information - where client PII was found
        updates.Client_Info_Links__c = value;
        updates.clientInfoLinks = value;
      }

      console.log('[PAW Save] Updates payload:', updates);
      console.log('[PAW Save] updatePAWRecord mutation status:', updatePAWRecord.status);
      console.log('[PAW Save] Calling updatePAWRecord.mutateAsync...');

      // Use mutateAsync instead of mutate for proper await/error handling
      try {
        const result = await updatePAWRecord.mutateAsync({ id: pawId, updates });
        console.log('[PAW Save] mutateAsync completed successfully:', result);

        // Remove from saving, add to saved (for brief success indicator)
        console.log('[PAW Save] Clearing saving state, setting saved state...');
        setSavingRows(prev => {
          const next = new Set(prev);
          next.delete(pawId);
          return next;
        });
        setSavedRows(prev => new Set(prev).add(pawId));

        // Clear success indicator after 1.5 seconds
        setTimeout(() => {
          setSavedRows(prev => {
            const next = new Set(prev);
            next.delete(pawId);
            return next;
          });
        }, 1500);

        await touchAuditMetadataFromChecklistEdit();

        // Background refetch to keep the paged UI current without blocking the save flow.
        console.log('[PAW Save] Starting background refetch...');
        setTimeout(() => {
          refetchVisiblePAWRecords().then(() => {
            console.log('[PAW Save] Background refetch completed');
          });
        }, 200);

        // If Findings changed, run business logic workflow (Flow 1 → 2 → 3)
        if (field === 'findings') {
          console.log('[Business Logic] Running audit workflow after Findings change...');
          const freshPAWData = await fetchAllPAWRecordsForAudit(auditId);

          // Create a fresh PAW fetcher that uses the just-refetched data (not stale pawRecordsRaw)
          const getFreshPAWsForWorkflow = async (): Promise<BusinessLogicPAWRecord[]> => {
            console.log('=== getFreshPAWsForWorkflow - USING FRESH DATA ===');
            console.log('Fresh PAW count:', freshPAWData.length);

            const mapped = freshPAWData.map((paw: any, idx: number) => {
              const data = paw.data ?? {};

              // Debug: Show ALL findings-related fields to find which one has the value
              console.log(`Fresh PAW ${idx} - ALL Findings fields:`, {
                'paw.Findings__c': paw.Findings__c,
                'paw.findings': paw.findings,
                'paw.Custom__findings__c': paw.Custom__findings__c,
                'data.Findings__c': data.Findings__c,
                'data.findings': data.findings,
                'data.Custom__findings__c': data.Custom__findings__c,
                'data.custom__findings': data.custom__findings,
              });

              // Try ALL possible field names for Findings
              const findingsValue =
                data.Findings__c ?? data.findings ?? data.Custom__findings__c ?? data.custom__findings ??
                paw.Findings__c ?? paw.findings ?? paw.Custom__findings__c ?? paw.custom__findings ?? null;

              const result = {
                id: String(paw.id ?? ''),
                Audit_Phase__c: (data.Audit_Phase__c ?? data.auditPhase ?? paw.Audit_Phase__c ?? paw.auditPhase ?? 'Phase 1') as 'Phase 1' | 'Phase 2',
                Findings__c: findingsValue,
                Phase_2_Required__c: Boolean(data.Phase_2_Required__c ?? data.phase2Required ?? paw.Phase_2_Required__c ?? paw.phase2Required ?? false),
                Audit_Template__c: data.Audit_Template__c ?? data.auditTemplate ?? paw.Audit_Template__c ?? paw.auditTemplate ?? undefined,
              };
              console.log(`Fresh PAW ${idx}: Findings="${result.Findings__c}", Phase="${result.Audit_Phase__c}"`);
              return result;
            });

            console.log('=== FRESH PAW SUMMARY ===');
            console.log('Total PAWs:', mapped.length);
            console.log('PAWs with Findings:', mapped.filter(p => p.Findings__c && p.Findings__c.trim() !== '').length);
            return mapped;
          };

          try {
            const workflowResult = await processAuditWorkflow(
              auditId,
              getAuditByIdForWorkflow,
              getFreshPAWsForWorkflow,  // Use fresh data instead of stale pawRecordsRaw
              getAccountByIdForWorkflow,
              updateAuditRecordForWorkflow,
              updatePAWRecordForWorkflow,
              updateAccountRecordForWorkflow,
              createCSAuditRecordForWorkflow,
              findExistingNextAuditForWorkflow
            );

            console.log('[Business Logic] Workflow result:', workflowResult);

            // Log status changes for visibility
            if (workflowResult.flow1Result.phaseStatusChanged) {
              console.log(`✅ Flow 1: Audit phase updated to: ${workflowResult.flow1Result.newPhaseStatus}`);
            }
            if (workflowResult.flow2Result.accountStatusChanged) {
              console.log(`✅ Flow 2: Account status updated to: ${workflowResult.flow2Result.newAccountStatus}`);
            }
            if (workflowResult.flow3Result.auditCreated) {
              console.log(`✅ Flow 3: New audit created: ${workflowResult.flow3Result.templateName} (ID: ${workflowResult.flow3Result.newAuditId})`);
            }
          } catch (workflowError) {
            console.error('[Business Logic] Workflow error:', workflowError);
          }
        }

        console.log('[PAW Save] handlePAWFieldChange COMPLETE SUCCESS');
        console.groupEnd();

      } catch (mutationError) {
        console.error('[PAW Save] mutateAsync FAILED:', mutationError);
        console.error('[PAW Save] Error type:', typeof mutationError);
        console.error('[PAW Save] Error message:', (mutationError as Error)?.message);
        console.error('[PAW Save] Error stack:', (mutationError as Error)?.stack);

        // Clear saving state on error
        setSavingRows(prev => {
          const next = new Set(prev);
          next.delete(pawId);
          return next;
        });
        console.groupEnd();
      }

    } catch (error) {
      console.error('[PAW Save] Outer try-catch ERROR:', error);
      console.error('[PAW Save] Error type:', typeof error);
      console.error('[PAW Save] Error message:', (error as Error)?.message);

      setSavingRows(prev => {
        const next = new Set(prev);
        next.delete(pawId);
        return next;
      });
      console.groupEnd();
    }
  };

  const tabs = [
    { id: 'related', label: 'Related' },
    { id: 'details', label: 'Details' },
    { id: 'notes', label: 'Notes' },
  ];

  const renderRelatedTab = () => (
    <div className="space-y-4">
      {/* Performed Audit Websites — SF related list */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-500" />
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">
              Performed Audit Websites ({pawRecordCount})
            </span>
          </div>
          <button
            onClick={() => setShowNewPAWModal(true)}
            className="px-3 py-1 text-xs font-medium border border-blue-500 text-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            New
          </button>
        </div>
        {pawRecordsLoading ? (
          <div className="px-6 py-8 flex flex-col items-center justify-center border-t border-gray-200 dark:border-gray-700">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading websites...</p>
          </div>
        ) : pawRecords.length > 0 ? (
          <>
            <div className="overflow-x-auto border-t border-gray-200 dark:border-gray-700">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="w-8 px-2 py-2">
                      <input
                        type="checkbox"
                        className="rounded"
                        checked={selectedPAWIds.size === pawRecords.length && pawRecords.length > 0}
                        onChange={(e) => handleSelectAllPAW(e.target.checked)}
                      />
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">PAW ID</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Website Name</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Audit Template</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Audit Phase</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {relatedPawRecords.map((paw) => (
                    <tr key={paw.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="w-8 px-2 py-2">
                        <input
                          type="checkbox"
                          className="rounded"
                          checked={selectedPAWIds.has(paw.id)}
                          onChange={() => handleTogglePAWSelection(paw.id)}
                        />
                      </td>
                      <td className="px-3 py-2 text-sm text-blue-600 cursor-pointer hover:underline font-medium"
                          onClick={() => setSelectedPAWRecord(paw)}>
                        PAW-{paw.id}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{renderPAWWebsiteValue(paw)}</td>
                      <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{paw.auditTemplate || '—'}</td>
                      <td className="px-3 py-2 text-sm">
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">{paw.auditPhase || '—'}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pawRecordsHasNextPage && (
              <div className="text-center py-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => pawRecordsInfiniteQuery.fetchNextPage()}
                  disabled={pawRecordsFetchingNextPage}
                  className="text-sm text-blue-600 hover:underline"
                >
                  {pawRecordsFetchingNextPage
                    ? 'Loading more websites...'
                    : `Load More (${pawRecords.length} of ${pawRecordCount})`}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
            No websites found for this audit.
          </div>
        )}
      </div>
    </div>
  );

  if (loadingAudit) {
    return (
      <GobodoRequiredGuard>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="animate-pulse text-gray-500 dark:text-gray-400">Loading audit...</div>
        </div>
      </GobodoRequiredGuard>
    );
  }

  if (!auditData) {
    return (
      <GobodoRequiredGuard>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-3">Unable to load this CS Audit record.</p>
            <button
              type="button"
              onClick={onBack}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Back
            </button>
          </div>
        </div>
      </GobodoRequiredGuard>
    );
  }

  const renderDetailsTab = () => (
    <div className="space-y-4">
      {/* Information section */}
      {sfSection('Information', [
        sfEditableTextRow('Audit Name', 'name', auditData.name || ''),
        sfEditableDateRow('Due Date', 'dueDate', auditData.dueDate || '', auditFieldDisplay(auditData.dueDate, 'date')),
        sfReadOnlyRow('Account',
          accountDisplayName !== '—' ? (
            <span className="text-blue-600 dark:text-blue-400 hover:underline cursor-pointer">{accountDisplayName}</span>
          ) : '—'
        ),
        sfEditableTextRow('Fiscal Year', 'fiscalYear', auditData.fiscalYear || ''),
        sfEditableSelectRow('Auditor', 'auditor', auditorDisplayName !== '—' ? auditorDisplayName : '', auditorOptions),
        sfEditableSelectRow('Audit Type', 'auditType', auditTypeDisplayName !== '—' ? auditTypeDisplayName : '', AUDIT_TYPE_OPTIONS),
      ])}

      {/* Phase History section */}
      {sfSection('Phase History', [
        sfEditableSelectRow('Phase Status', 'phaseStatus', auditData.phaseStatus || '', PHASE_STATUS_OPTIONS),
        sfEditableTextRow('Road Block Note', 'roadBlockNote', auditData.roadBlockNote || auditData.roadblockNotes || ''),
        sfEditableDateRow('Phase 1 Due Date', 'phase1DueDate', auditData.phase1DueDate || '', auditFieldDisplay(auditData.phase1DueDate, 'date')),
        sfEditableDateRow('Phase 2 Due Date', 'phase2DueDate', auditData.phase2DueDate || '', auditFieldDisplay(auditData.phase2DueDate, 'date')),
        sfEditableDateRow('Phase 1 Start Date/Time', 'phase1StartDateTime', auditData.phase1StartDateTime || '', auditFieldDisplay(auditData.phase1StartDateTime, 'datetime')),
        sfEditableDateRow('Phase 2 Start Date/Time', 'phase2StartDateTime', auditData.phase2StartDateTime || '', auditFieldDisplay(auditData.phase2StartDateTime, 'datetime')),
        sfEditableDateRow('Phase 1 Completed Date/Time', 'phase1CompletedDateTime', auditData.phase1CompletedDateTime || '', auditFieldDisplay(auditData.phase1CompletedDateTime, 'datetime')),
        sfEditableDateRow('Phase 2 Completed Date/Time', 'phase2CompletedDateTime', auditData.phase2CompletedDateTime || '', auditFieldDisplay(auditData.phase2CompletedDateTime, 'datetime')),
      ])}

      {/* System Information section */}
      {sfSection('System Information', [
        sfReadOnlyRow('Created By', createdByDisplay),
        sfReadOnlyRow('Created Date', createdDateDisplay),
        sfReadOnlyRow('Last Modified By', lastModifiedByDisplay),
        sfReadOnlyRow('Last Modified Date', lastModifiedDateDisplay),
      ])}
    </div>
  );

  const renderNotesTab = () => (
    <div className="space-y-4">
      {/* Notes — SF related list */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-500" />
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Notes ({notes.length})</span>
          </div>
          <button
            onClick={() => setIsAddingNote(true)}
            className="px-3 py-1 text-xs font-medium border border-blue-500 text-blue-600 rounded hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            New
          </button>
        </div>

        {/* Content */}
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Add Note Form */}
          {isAddingNote && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
              <StandardTextarea
                placeholder="Enter your note..."
                rows={3}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
              <div className="flex justify-end gap-2 mt-3">
                <button
                  onClick={() => { setIsAddingNote(false); setNewNote(''); }}
                  className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddNote}
                  disabled={!newNote.trim()}
                  className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {/* Notes List */}
          {notes.length > 0 ? (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {notes.map((note) => (
                <div key={note.id} className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 group">
                  {editingNoteId === note.id ? (
                    /* Edit Mode */
                    <div>
                      <StandardTextarea
                        value={editNoteValue}
                        onChange={(e) => setEditNoteValue(e.target.value)}
                        rows={3}
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <button
                          onClick={handleCancelEditNote}
                          className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveEditedNote}
                          disabled={!editNoteValue.trim()}
                          className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap">{note.body}</p>
                        {/* System Information */}
                        <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Created By: </span>
                              <span className="font-medium text-blue-600 dark:text-blue-400">{note.createdBy}</span>
                              <span className="text-gray-400 dark:text-gray-500">, {formatNoteDate(note.createdDate)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 dark:text-gray-400">Last Modified By: </span>
                              {note.lastModifiedBy ? (
                                <>
                                  <span className="font-medium text-blue-600 dark:text-blue-400">{note.lastModifiedBy}</span>
                                  <span className="text-gray-400 dark:text-gray-500">, {formatNoteDate(note.lastModifiedDate || '')}</span>
                                </>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleStartEditNote(note)}
                        className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-opacity flex-shrink-0"
                        title="Edit note"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : !isAddingNote && (
            <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
              No notes to display.
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* SF-style blue header bar */}
      <div className="bg-blue-600 dark:bg-blue-800 rounded-lg px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="text-white/80 hover:text-white mr-1">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="bg-blue-500 dark:bg-blue-700 rounded-lg p-2">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <div>
            <p className="text-xs text-blue-200">CS Audit</p>
            <h1 className="text-lg font-bold text-white">{auditData.name || auditData.id || 'CS Audit'}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={async () => {
              // Force refetch when opening modal to ensure we have latest PAW records
              // This prevents duplicates from being added
              console.log('[AddAuditChecklist] Refreshing PAW records before opening modal...');
              try {
                const result = await refetchPAWRecords();
                setExistingChecklistWebsites(extractExistingWebsiteNames((result.data as Record<string, unknown>[]) ?? []));
              } catch (err) {
                console.warn('[AddAuditChecklist] Refetch failed, opening modal anyway:', err);
                setExistingChecklistWebsites(extractExistingWebsiteNames(pawRecordsRaw as Record<string, unknown>[]));
              }
              setShowAddChecklistModal(true);
            }}
            className="px-3 py-1.5 text-xs font-medium border border-white/60 text-white rounded hover:bg-white/10"
          >
            Add Audit Checklist
          </button>
          <button
            onClick={() => setShowClientProfileModal(true)}
            className="px-3 py-1.5 text-xs font-medium border border-white/60 text-white rounded hover:bg-white/10"
          >
            CS Client Profile
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="px-3 py-1.5 text-xs font-medium border border-red-400/60 text-red-200 rounded hover:bg-red-500/20 flex items-center gap-1"
            title="Delete this CS Audit"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Delete CS Audit?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this CS Audit record? This action cannot be undone.
              All associated PAW records will remain but will no longer be linked to this audit.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setIsDeleting(true);
                  try {
                    const auditRecordId = String((auditRecord as any)?.id ?? auditId);
                    await deleteCSAudit.mutateAsync(auditRecordId);
                    setShowDeleteConfirm(false);
                    onBack(); // Navigate back after deletion
                  } catch (err) {
                    console.error('[DeleteCSAudit] Error deleting audit:', err);
                    alert('Failed to delete CS Audit. Please try again.');
                  } finally {
                    setIsDeleting(false);
                  }
                }}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab card */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {/* Tab navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab content */}
        <div className="p-6">
          {activeTab === 'related' && renderRelatedTab()}
          {activeTab === 'details' && renderDetailsTab()}
          {activeTab === 'notes' && renderNotesTab()}
        </div>
      </div>

      {/* Audit Checklist — persistent below tabs (appears on every tab) */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Audit Checklist</h2>
          <button
            onClick={() => alert('Coming Soon: Automated website processing')}
            className="px-3 py-1.5 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Coming Soon"
          >
            Process Websites
          </button>
        </div>
        {pawRecordsLoading ? (
          <div className="px-6 py-12 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-3"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading audit checklist...</p>
          </div>
        ) : pawRecords.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Website Name</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Audit Template</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phase</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-56">Findings</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-40">PII</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-48">Link(s)</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-20">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {sortedPawRecords.map((paw) => {
                  const isSaving = savingRows.has(paw.id);
                  const isSaved = savedRows.has(paw.id);

                  return (
                    <tr key={paw.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isSaving ? 'bg-blue-50 dark:bg-blue-900/20' : ''} ${isSaved ? 'bg-green-50 dark:bg-green-900/20' : ''}`}>
                      {/* Website Name - Read Only */}
                      <td className="px-3 py-2 text-sm">
                        <div className="flex items-center gap-2">
                          {renderPAWWebsiteValue(paw, true)}
                          {isSaving && (
                            <span className="inline-flex items-center text-xs text-blue-600">
                              <div className="animate-spin h-3 w-3 border-2 border-blue-600 border-t-transparent rounded-full mr-1"></div>
                              Saving...
                            </span>
                          )}
                          {isSaved && (
                            <span className="inline-flex items-center text-xs text-green-600">
                              <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                              Saved
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Audit Template - Read Only */}
                      <td className="px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
                        {paw.auditTemplate || '—'}
                      </td>

                      {/* Audit Phase - Read Only */}
                      <td className="px-3 py-2 text-sm">
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 rounded-full">
                          {paw.auditPhase || '—'}
                        </span>
                      </td>

                      {/* Findings - Editable Dropdown (auto-save on change) */}
                      <td className="px-3 py-2 text-sm">
                        <select
                          value={paw.findings || ''}
                          onChange={(e) => handlePAWFieldChange(paw.id, 'findings', e.target.value)}
                          disabled={isSaving}
                          className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
                        >
                          {FINDINGS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>

                      {/* PII - Editable Multiselect (save on explicit action) */}
                      <td className="px-3 py-2 text-sm">
                        <ChecklistPiiMultiSelect
                          value={paw.piiFound}
                          disabled={isSaving}
                          options={PII_OPTIONS}
                          onSave={(selectedValues) => {
                            const nextValue = serializePiiFoundValue(selectedValues);
                            if (nextValue !== serializePiiFoundValue(parsePiiFoundValue(paw.piiFound))) {
                              handlePAWFieldChange(paw.id, 'piiFound', selectedValues);
                            }
                          }}
                        />
                      </td>

                      {/* Link(s) for client information - Editable Textarea (auto-save on blur) */}
                      <td className="px-3 py-2 text-sm">
                        {(() => {
                          // Convert clientInfoLinks to string for display (handles both string and array)
                          const linksValue = Array.isArray(paw.clientInfoLinks)
                            ? paw.clientInfoLinks.join('\n')
                            : (paw.clientInfoLinks || '');
                          return (
                            <textarea
                              key={`links-${paw.id}-${linksValue}`}
                              defaultValue={linksValue}
                              onBlur={(e) => {
                                const currentValue = e.target.value;
                                console.log('[Link(s)] onBlur - currentValue:', currentValue, 'savedValue:', linksValue);
                                if (currentValue !== linksValue) {
                                  console.log('[Link(s)] Value changed, triggering save to clientInfoLinks...');
                                  handlePAWFieldChange(paw.id, 'clientInfoLinks', currentValue);
                                } else {
                                  console.log('[Link(s)] No change detected, skipping save');
                                }
                              }}
                              disabled={isSaving}
                              placeholder="Enter URLs (one per line)"
                              rows={2}
                              className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-600 rounded bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-gray-100 hover:border-blue-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 disabled:opacity-50 resize-y"
                            />
                          );
                        })()}
                      </td>

                      {/* Actions */}
                      <td className="px-3 py-2 text-sm">
                        <button
                          onClick={() => setSelectedPAWRecord(paw)}
                          className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            {pawRecordsHasNextPage && (
              <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-700 text-center">
                <button
                  onClick={() => pawRecordsInfiniteQuery.fetchNextPage()}
                  disabled={pawRecordsFetchingNextPage}
                  className="text-sm text-blue-600 hover:underline disabled:opacity-50"
                >
                  {pawRecordsFetchingNextPage
                    ? 'Loading more audit checklist rows...'
                    : `Load More (${pawRecords.length} of ${pawRecordCount})`}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="px-6 py-8 text-center text-sm text-gray-500">No websites in checklist yet.</div>
        )}
      </div>

      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={auditId}
        recordType="client"
        recipientEmail=""
        recipientName={auditData.clientName}
        onEmailSent={() => {}}
        department="cybersweep"
        emailContext={{
          client: {
            id: auditData.clientId,
            name: auditData.clientName,
            email: '',
            phone: '',
            segment: '',
            status: auditData.status,
            owner: auditData.assignedAnalyst,
            lastContact: '',
            riskTier: '',
            accountName: '',
            accountOwner: '',
            accountManager: '',
          },
          system: {
            date: new Date().toLocaleDateString(),
            time: new Date().toLocaleTimeString(),
            url: typeof window !== 'undefined' ? window.location.origin : '',
            companyName: 'Regal Credit Management',
          },
        }}
      />

      {/* PAW Record Detail Modal */}
      {selectedPAWRecord && (
        <PAWRecordDetail
          record={selectedPAWRecord}
          onClose={() => setSelectedPAWRecord(null)}
          csAuditDisplayName={auditData?.id}
          onSave={() => refetchPAWRecords()}
        />
      )}

      {/* CS Audit Edit Modal */}
      <CSAuditEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        auditData={auditData}
        onSave={handleSaveAudit}
      />

      {/* New PAW Record Modal */}
      <NewPAWRecordModal
        isOpen={showNewPAWModal}
        onClose={() => setShowNewPAWModal(false)}
        auditId={auditId}
        auditName={auditData.name || auditData.id || ''}
        onSave={async (data) => {
          console.log('[NewPAW] Creating PAW record:', data);
          // Map form data to Gobodo/Salesforce field names
          const piiFoundValue = Array.isArray(data.piiFound) ? data.piiFound.join(';') : '';
          // Auto-generate the PAW Name (Salesforce auto-number format)
          const autoGeneratedName = `PAW-${Date.now()}`;
          const mappedData: Record<string, unknown> = {
            // Name is auto-generated, NOT from user input
            Name: autoGeneratedName,
            // Website__c stores the URL (e.g., "google.com")
            Website__c: data.website || '',
            CS_Audit__c: auditId,
            // Relationship field for Gobodo graph traversal (links PAW to CSAudit)
            [`paw_cs_audit_rel${getCustomSuffix()}`]: auditId,
            Audit_Phase__c: data.auditPhase !== '--None--' ? data.auditPhase : '',
            Findings__c: data.findings !== '--None--' ? data.findings : '',
            Method__c: data.method !== '--None--' ? data.method : '',
            Phase_2_Required__c: data.phase2Required || false,
            PII_Found__c: piiFoundValue,
            Client_Info_Links__c: data.clientInfoLinks || '',
          };
          console.log('[NewPAW] Mapped data being sent:', mappedData);
          try {
            await createPAWRecord.mutateAsync(mappedData);
            console.log('[NewPAW] PAW record created successfully');
            setShowNewPAWModal(false);
          } catch (error) {
            console.error('[NewPAW] Failed to create PAW record:', error);
          }
        }}
      />

      {/* Add Audit Checklist Modal */}
        <AddAuditChecklistModal
        isOpen={showAddChecklistModal}
        onClose={() => {
          setChecklistBatchProgress(null);
          setShowAddChecklistModal(false);
        }}
        auditId={auditId}
        currentTemplate={auditData?.auditType}
        existingWebsites={existingChecklistWebsites}
        batchProgress={checklistBatchProgress}
        onAddWebsites={async (websites, templateName) => {
          // Robust duplicate check - filter out any websites that already exist
          const existingSet = new Set(
            existingChecklistWebsites
              .map((name) => name.toLowerCase().trim())
              .filter((name) => name.length > 0)
          );

          const uniqueWebsites = websites.filter(w =>
            !existingSet.has(w.websiteCompany.toLowerCase().trim())
          );

          if (uniqueWebsites.length < websites.length) {
            console.log('[AddAuditChecklist] Filtered out', websites.length - uniqueWebsites.length, 'duplicate websites');
          }

          if (uniqueWebsites.length === 0) {
            console.log('[AddAuditChecklist] All websites already exist, nothing to add');
            return;
          }

          console.log('[AddAuditChecklist] Adding', uniqueWebsites.length, 'websites with template:', templateName);
          const checklistPhaseDefaults = deriveChecklistPhaseDefaults(auditData?.phaseStatus);
          console.log('[AddAuditChecklist] Derived checklist phase defaults:', checklistPhaseDefaults);
          const base = Date.now();
          const mappedList: Record<string, unknown>[] = uniqueWebsites.map((website, idx) => ({
            Name: `PAW-${base}-${idx}-${Math.random().toString(36).substring(2, 7)}`,
            Website__c: website.websiteCompany,
            websiteDisplayName: website.websiteCompany,
            Website_URL__c: website.url,
            websiteUrl: website.url,
            CS_Audit__c: auditId,
            // Relationship field for Gobodo graph traversal (links PAW to CSAudit)
            [`paw_cs_audit_rel${getCustomSuffix()}`]: auditId,
            Audit_Template__c: templateName,
            Audit_Phase__c: checklistPhaseDefaults.auditPhase,
            Method__c: '',
            Phase_2_Required__c: checklistPhaseDefaults.phase2Required,
            PII_Found__c: '',
            Client_Info_Links__c: '',
          }));
          try {
            await createPAWRecordsBatch.mutateAsync(mappedList);
            console.log('[AddAuditChecklist] Created', mappedList.length, 'PAW records');
          } catch (error) {
            console.error('[AddAuditChecklist] Batch create failed:', error);
            // Don't throw - records may have been partially created
            // The refetch below will show what was actually created
          } finally {
            setChecklistBatchProgress(null);
            // Always refetch to ensure UI shows current state
            // This handles partial success cases (504 timeout) and ensures
            // proper enrichment of records that cache merge might miss
            console.log('[AddAuditChecklist] Refreshing PAW records list...');
            const result = await refetchPAWRecords();
            setExistingChecklistWebsites(extractExistingWebsiteNames((result.data as Record<string, unknown>[]) ?? []));
          }
        }}
      />

      {/* CS Client Profile Modal */}
      <CSClientProfileModal
        isOpen={showClientProfileModal}
        onClose={() => setShowClientProfileModal(false)}
        clientData={clientProfileData}
        onViewFullRecord={() => {
          // Close modal and navigate to client detail page
          setShowClientProfileModal(false);
          // Navigate to client detail - use onBack with client context
          // The parent (CyberSweepApp) handles the navigation
          if (auditData?.clientId) {
            // For now, just close the modal - the full navigation can be wired up
            // when the onViewClient callback is added to the component props
            console.log('[CSClientProfile] View full record:', auditData.clientId);
          }
        }}
      />

      {/* Website Info Modal */}
      {websiteInfoName && (
        <WebsiteInfoModal
          websiteName={websiteInfoName}
          onClose={() => setWebsiteInfoName(null)}
        />
      )}
    </div>
  );
};

export default CyberSweepAuditDetail;
